import React, { useEffect, useRef } from 'react';
import { 
  FiUsers, FiTarget, FiAward, FiGlobe, FiHeart, 
  FiTrendingUp, FiShield, FiClock, FiStar, FiChevronRight,
  FiTarget as FiTargetIcon, FiGlobe as FiGlobeIcon, FiZap
} from 'react-icons/fi';
import { FaLinkedinIn, FaTwitter } from 'react-icons/fa';
import { motion, useInView, useAnimation } from 'framer-motion';
import AboutHero from './AboutHero';
import AboutFeatures from './AboutFeatures';
import WhereWeWork from './WhereWeWork';
import useSEO from "../utils/useSEO";


const AboutUsPage = () => {
  // Mission & Vision Animation Refs
  const missionVisionRef = useRef(null);
  const missionVisionInView = useInView(missionVisionRef, { once: true, amount: 0.3 });
  const missionVisionControls = useAnimation();
 useSEO({
    title: "About Humanex | Recruitment & HR Solutions Experts",
    description:
      "Learn how Humanex supports organizations with recruitment, staffing, and onboarding solutions focused on quality hiring, efficiency, and growth.",
  });
  useEffect(() => {
    if (missionVisionInView) {
      missionVisionControls.start("visible");
    }
  }, [missionVisionInView, missionVisionControls]);

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6
      }
    }
  };

  const iconVariants = {
    hidden: { 
      scale: 0,
      rotate: -180
    },
    visible: { 
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        duration: 0.8
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.4,
        duration: 0.6
      }
    }
  };

  // Floating Elements for Mission & Vision
  const floatingElements = [
    { icon: <FiUsers />, delay: 0, position: { top: '10%', left: '5%' } },
    { icon: <FiTrendingUp />, delay: 0.3, position: { top: '15%', right: '8%' } },
    { icon: <FiAward />, delay: 0.6, position: { bottom: '20%', left: '7%' } },
    { icon: <FiShield />, delay: 0.9, position: { bottom: '10%', right: '6%' } }
  ];

  // Team Data
  const teamMembers = [
    {
      name: "Sarah Chen",
      role: "CEO & Founder",
      bio: "Former HR Director at Google with 15+ years experience in HR tech",
      image: "https://images.unsplash.com/photo-1494790108755-2616b786d4d2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80"
    },
    {
      name: "Marcus Johnson",
      role: "CTO",
      bio: "AI/ML specialist with focus on HR automation and predictive analytics",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80"
    },
    {
      name: "Priya Sharma",
      role: "Head of Product",
      bio: "Product leader with expertise in building enterprise HR solutions",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80"
    },
    {
      name: "David Park",
      role: "Head of Customer Success",
      bio: "HR consultant turned tech leader focused on client satisfaction",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80"
    }
  ];

  // Statistics Data
  const statistics = [
    { number: "10,000+", label: "Active Customers", icon: <FiUsers /> },
    { number: "50+", label: "Countries Worldwide", icon: <FiGlobe /> },
    { number: "99.9%", label: "Uptime Guarantee", icon: <FiShield /> },
    { number: "24/7", label: "Customer Support", icon: <FiClock /> }
  ];

  // Achievements Data
  const achievements = [
    {
      year: "2024",
      title: "Best HR Tech Platform",
      organization: "HR Tech Awards",
      icon: <FiAward />
    },
    {
      year: "2023",
      title: "Fastest Growing SaaS",
      organization: "Tech Growth 500",
      icon: <FiTrendingUp />
    },
    {
      year: "2023",
      title: "Customer Choice Award",
      organization: "G2 Crowd",
      icon: <FiStar />
    }
  ];

  // Values Data
  const values = [
    {
      icon: <FiUsers />,
      title: "People First",
      description: "We believe technology should enhance human connections, not replace them."
    },
    {
      icon: <FiTarget />,
      title: "Innovation",
      description: "Continuously pushing boundaries in HR technology to solve real problems."
    },
    {
      icon: <FiShield />,
      title: "Integrity",
      description: "Maintaining the highest standards of security and ethical practices."
    },
    {
      icon: <FiHeart />,
      title: "Empathy",
      description: "Understanding the human experience behind every HR process."
    }
  ];

  return (
    <div className="Humanex-about">
      {/* Hero Section */}
<AboutHero/>

      {/* Statistics Section */}


      {/* Mission & Vision */}


      {/* Our Story */}

<AboutFeatures/>
<WhereWeWork/>

      {/* Our Values */}


      {/* Achievements Section */}


      {/* Leadership Team */}


      {/* Company Culture Section */}
      <section className="above-cultures">
        <div className="above-culture-container">
          <motion.div
            className="above-culture-content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="above-culture-badge">Our Culture</span>
            <h2 className="above-culture-title">
              Where Innovation Meets <span className="above-text-highlight">Inclusion</span>
            </h2>
            <p className="above-culture-description">
              At Humanex, we believe that great products come from great teams. We've built 
              a culture that celebrates diversity, encourages creativity, and empowers every 
              team member to make an impact.
            </p>
            <div className="above-culture-features">
              <div className="above-culture-feature">
                <FiHeart className="above-feature-icon" />
                <div>
                  <h4>Work-Life Balance</h4>
                  <p>Flexible schedules and remote work options</p>
                </div>
              </div>
              <div className="above-culture-feature">
                <FiTrendingUp className="above-feature-icon" />
                <div>
                  <h4>Growth Opportunities</h4>
                  <p>Continuous learning and career development</p>
                </div>
              </div>
              <div className="above-culture-feature">
                <FiUsers className="above-feature-icon" />
                <div>
                  <h4>Inclusive Environment</h4>
                  <p>Diverse team from 25+ countries</p>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div 
            className="above-culture-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src="/images/home1.png" 
              alt="Team collaboration" 
            />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}


      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&family=Inter:wght@300;400;500;600&display=swap');

        .Humanex-about {
          font-family: 'Inter', sans-serif;
          color: #1e2330;
        }

      /* Hero Section */
   .about-hero {
  position: relative;
  background: linear-gradient(135deg, #fffaf4 0%, #fffaf4 100%);
  padding: 100px 0 60px;
}

.about-hero-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.about-hero-title {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #1e293b;
}

.about-hero-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.about-hero-cta {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
}

.btn {
  padding: 12px 28px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  border-color: #cbd5e1;
  color: #475569;
}

.btn-outline:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  transform: translateY(-2px);
}

.about-stats-section {
  padding: 60px 0;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.about-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.stat-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
}

.stat-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.stat-icon {
  width: 48px;
  height: 48px;
  background: #eff6ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #3b82f6;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 1.125rem;
  font-weight: 600;
  color: #475569;
  margin-top: 0.25rem;
}

.stat-description {
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.25rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .about-stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

@media (max-width: 640px) {
  .about-hero-title {
    font-size: 2.5rem;
  }
  
  .about-hero-subtitle {
    font-size: 1.125rem;
  }
  
  .about-hero-cta {
    flex-direction: column;
    align-items: center;
  }
  
  .about-stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .stat-card {
    padding: 1.5rem;
  }
}
    /* Enhanced Mission & Vision */
    .enhanced-mission-vision {
      position: relative;
      padding: 120px 20px;
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      overflow: hidden;
    }

    .mv-bg-gradient {
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at 50% 50%, rgba(198, 40, 40, 0.05) 0%, transparent 70%);
      pointer-events: none;
    }

    .mv-grid-pattern {
      position: absolute;
      inset: 0;
      background-image: 
        linear-gradient(rgba(198, 40, 40, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(198, 40, 40, 0.03) 1px, transparent 1px);
      background-size: 60px 60px;
      opacity: 0.5;
    }

    .mv-floating-element {
      position: absolute;
      font-size: 48px;
      color: rgba(198, 40, 40, 0.1);
      z-index: 0;
    }

    .mv-container {
      position: relative;
      max-width: 1200px;
      margin: 0 auto;
      z-index: 1;
    }

    .mv-header {
      text-align: center;
      margin-bottom: 80px;
    }

    .mv-badge {
      display: inline-block;
      background: rgba(198, 40, 40, 0.1);
      color: #c62828;
      padding: 10px 24px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 20px;
    }

    .mv-title {
      font-family: 'Raleway', sans-serif;
      font-size: 48px;
      font-weight: 800;
      color: #1e2330;
      margin-bottom: 16px;
    }

    .mv-title-highlight {
      color: #c62828;
      position: relative;
    }

    .mv-title-highlight::after {
      content: '';
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 8px;
      background: rgba(198, 40, 40, 0.15);
      z-index: -1;
      border-radius: 4px;
    }

    .mv-subtitle {
      font-size: 18px;
      color: #4a4f5c;
      max-width: 600px;
      margin: 0 auto;
    }

    .mv-cards-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
      gap: 40px;
      perspective: 1000px;
    }

    .mv-card {
      position: relative;
      background: white;
      padding: 50px 40px;
      border-radius: 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      border: 1px solid rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;
    }

    .mv-card-background {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, transparent 30%, rgba(198, 40, 40, 0.02) 100%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .mv-card:hover .mv-card-background {
      opacity: 1;
    }

    .mv-card-icon-wrapper {
      position: relative;
      width: 100px;
      height: 100px;
      margin: 0 auto 30px;
    }

    .mv-icon-ring {
      position: relative;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #c62828, #ff5252);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
    }

    .mv-icon {
      color: white;
      font-size: 40px;
    }

    .mv-icon-glow {
      position: absolute;
      inset: -10px;
      background: rgba(198, 40, 40, 0.2);
      border-radius: 50%;
      filter: blur(20px);
      z-index: 1;
      animation: iconGlow 3s ease-in-out infinite;
    }

    @keyframes iconGlow {
      0%, 100% { opacity: 0.5; transform: scale(1); }
      50% { opacity: 0.8; transform: scale(1.1); }
    }

    .mv-card-title {
      font-family: 'Raleway', sans-serif;
      font-size: 32px;
      font-weight: 700;
      color: #1e2330;
      text-align: center;
      margin-bottom: 24px;
      position: relative;
    }

    .mv-title-line {
      display: block;
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #c62828, transparent);
      margin: 12px auto 0;
      border-radius: 2px;
    }

    .mv-card-content {
      font-size: 18px;
      line-height: 1.8;
      color: #4a4f5c;
      text-align: center;
      margin-bottom: 30px;
    }

    .mv-card-highlights {
      display: flex;
      justify-content: center;
      gap: 20px;
      flex-wrap: wrap;
      margin-top: 30px;
    }

    .mv-highlight {
      background: rgba(198, 40, 40, 0.08);
      color: #c62828;
      padding: 8px 20px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 600;
    }

    .mv-particles {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }

    .mv-particle {
      position: absolute;
      width: 4px;
      height: 4px;
      background: #c62828;
      border-radius: 50%;
      opacity: 0;
    }

    .mv-particle:nth-child(1) { top: 20%; left: 10%; }
    .mv-particle:nth-child(2) { top: 30%; right: 15%; }
    .mv-particle:nth-child(3) { bottom: 25%; left: 20%; }
    .mv-particle:nth-child(4) { bottom: 15%; right: 25%; }
    .mv-particle:nth-child(5) { top: 50%; left: 50%; }

    .mission-card .mv-icon-ring {
      background: linear-gradient(135deg, #c62828, #ff5252);
    }

    .vision-card .mv-icon-ring {
      background: linear-gradient(135deg, #2563eb, #60a5fa);
    }

    .vision-card .mv-icon-glow {
      background: rgba(37, 99, 235, 0.2);
    }

    .vision-card .mv-highlight {
      background: rgba(37, 99, 235, 0.08);
      color: #2563eb;
    }

    /* Our Story */
    .enhanced-our-story {
      padding: 120px 20px;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      position: relative;
      overflow: hidden;
    }

    .enhanced-our-story::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 500px;
      height: 500px;
      background: radial-gradient(circle, rgba(198, 40, 40, 0.05) 0%, transparent 70%);
      filter: blur(60px);
      z-index: 0;
    }

    .story-container {
      max-width: 1400px;
      margin: 0 auto;
      position: relative;
      z-index: 1;
    }

    .story-header {
      text-align: center;
      margin-bottom: 80px;
    }

    .story-badge {
      display: inline-block;
      background: linear-gradient(135deg, #c62828, #ff5252);
      color: white;
      padding: 10px 28px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 24px;
      box-shadow: 0 8px 25px rgba(198, 40, 40, 0.2);
    }

    .story-title {
      font-family: 'Raleway', sans-serif;
      font-size: 56px;
      font-weight: 900;
      color: #1e2330;
      margin-bottom: 16px;
      line-height: 1.2;
    }

    .story-title-highlight {
      color: #c62828;
      position: relative;
    }

    .story-title-highlight::after {
      content: '';
      position: absolute;
      bottom: 8px;
      left: 0;
      width: 100%;
      height: 12px;
      background: rgba(198, 40, 40, 0.15);
      z-index: -1;
      border-radius: 6px;
    }

    .story-subtitle {
      font-size: 20px;
      color: #4a4f5c;
      max-width: 600px;
      margin: 0 auto;
      line-height: 1.6;
    }

    .story-content-wrapper {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 80px;
      align-items: start;
      margin-bottom: 80px;
    }

    .story-left {
      position: relative;
    }

    .story-timeline {
      position: absolute;
      left: 40px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: linear-gradient(to bottom, #c62828, #2563eb);
      z-index: 1;
    }

    .timeline-dot {
      position: absolute;
      left: -14px;
      width: 30px;
      height: 30px;
      background: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      z-index: 2;
    }

    .timeline-dot[data-year="2018"] {
      top: 20%;
    }

    .timeline-dot[data-year="2024"] {
      top: 80%;
    }

    .timeline-dot::before {
      content: attr(data-year);
      position: absolute;
      left: -100px;
      top: 50%;
      transform: translateY(-50%);
      font-family: 'Raleway', sans-serif;
      font-size: 24px;
      font-weight: 900;
      color: #c62828;
      white-space: nowrap;
    }

    .dot-inner {
      width: 14px;
      height: 14px;
      background: linear-gradient(135deg, #c62828, #ff5252);
      border-radius: 50%;
    }

    .story-cards {
      display: flex;
      flex-direction: column;
      gap: 40px;
      padding-left: 80px;
    }

    .story-card {
      background: white;
      padding: 40px;
      border-radius: 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(0, 0, 0, 0.05);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
    }

    .story-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: linear-gradient(to bottom, #c62828, #2563eb);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .story-card:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
    }

    .story-card:hover::before {
      opacity: 1;
    }

    .card-icon {
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #c62828, #ff5252);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 24px;
      color: white;
      font-size: 24px;
    }

    .card-2 .card-icon {
      background: linear-gradient(135deg, #2563eb, #60a5fa);
    }

    .card-title {
      font-family: 'Raleway', sans-serif;
      font-size: 24px;
      font-weight: 700;
      color: #1e2330;
      margin-bottom: 16px;
    }

    .card-text {
      font-size: 16px;
      line-height: 1.8;
      color: #4a4f5c;
      margin-bottom: 24px;
    }

    .card-tags {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .tag {
      background: rgba(198, 40, 40, 0.08);
      color: #c62828;
      padding: 6px 16px;
      border-radius: 50px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .card-2 .tag {
      background: rgba(37, 99, 235, 0.08);
      color: #2563eb;
    }

    .story-right {
      position: relative;
    }

    .image-container {
      position: relative;
    }

    .image-wrapper {
      position: relative;
      border-radius: 24px;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      margin-bottom: 24px;
    }

    .story-main-image {
      width: 100%;
      height: 500px;
      object-fit: cover;
      display: block;
      transition: transform 0.6s ease;
    }

    .image-wrapper:hover .story-main-image {
      transform: scale(1.05);
    }

    .image-gradient-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.3), transparent 50%);
      pointer-events: none;
    }

    .floating-stat {
      position: absolute;
      background: white;
      padding: 20px;
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 15px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.8);
      z-index: 2;
    }

    .stat-1 {
      top: 40px;
      right: 40px;
    }

    .stat-2 {
      bottom: 100px;
      left: 40px;
    }

    .stat-3 {
      bottom: 40px;
      right: 40px;
    }

    .stat-icon {
      width: 50px;
      height: 50px;
      background: linear-gradient(135deg, #c62828, #ff5252);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 20px;
    }

    .stat-2 .stat-icon {
      background: linear-gradient(135deg, #2563eb, #60a5fa);
    }

    .stat-3 .stat-icon {
      background: linear-gradient(135deg, #ffa726, #ffb74d);
    }

    .stat-content {
      display: flex;
      flex-direction: column;
    }

    .stat-value {
      font-family: 'Raleway', sans-serif;
      font-size: 28px;
      font-weight: 900;
      color: #1e2330;
      line-height: 1;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 14px;
      color: #666;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .image-caption {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: white;
      padding: 30px;
      border-radius: 20px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    }

    .caption-text h4 {
      font-family: 'Raleway', sans-serif;
      font-size: 22px;
      font-weight: 700;
      color: #1e2330;
      margin-bottom: 8px;
    }

    .caption-text p {
      font-size: 16px;
      color: #4a4f5c;
      line-height: 1.6;
    }

    .achievement-badges {
      display: flex;
      gap: 16px;
    }

    .badge {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(198, 40, 40, 0.08);
      padding: 10px 20px;
      border-radius: 50px;
      color: #c62828;
      font-size: 14px;
      font-weight: 600;
    }

    .badge svg {
      font-size: 18px;
    }

    /* Our Values */
    .our-values {
      padding: 80px 20px;
      background: #ffffff;
    }

    .values-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    .values-header {
      text-align: center;
      margin-bottom: 60px;
    }

    .values-header h2 {
      font-family: 'Raleway', sans-serif;
      font-size: 42px;
      font-weight: 800;
      margin-bottom: 16px;
      color: #1e2330;
    }

    .values-header p {
      font-size: 18px;
      color: #4a4f5c;
      max-width: 600px;
      margin: 0 auto;
    }

    .values-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 30px;
    }

    .value-card {
      background: #f8f9fa;
      padding: 40px 30px;
      border-radius: 16px;
      text-align: center;
      transition: all 0.3s ease;
    }

    .value-card:hover {
      background: #ffffff;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
      transform: translateY(-5px);
    }

    .value-icon {
      font-size: 40px;
      margin-bottom: 20px;
    }

    .value-card h3 {
      font-family: 'Raleway', sans-serif;
      font-size: 22px;
      font-weight: 700;
      margin-bottom: 12px;
      color: #1e2330;
    }

    .value-card p {
      color: #4a4f5c;
      line-height: 1.6;
      font-size: 15px;
    }

    /* Achievements Section */
    .above-achievements {
      padding: 100px 20px;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    }

    .above-achievements-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    .above-achievements-header {
      text-align: center;
      margin-bottom: 60px;
    }

    .above-achievements-badge {
      display: inline-block;
      background: linear-gradient(135deg, #c62828, #ff5252);
      color: #ffffff;
      padding: 10px 28px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 24px;
      box-shadow: 0 8px 25px rgba(198, 40, 40, 0.2);
    }

    .above-achievements-title {
      font-family: 'Raleway', sans-serif;
      font-size: 48px;
      font-weight: 900;
      color: #1e2330;
      margin-bottom: 16px;
      line-height: 1.2;
    }

    .above-text-highlight {
      color: #c62828;
      position: relative;
    }

    .above-text-highlight::after {
      content: '';
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 12px;
      background: rgba(198, 40, 40, 0.15);
      z-index: -1;
      border-radius: 6px;
    }

    .above-achievements-subtitle {
      font-size: 20px;
      color: #4a4f5c;
      max-width: 600px;
      margin: 0 auto;
    }

    .above-achievements-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 40px;
    }

    .above-achievement-card {
      background: #ffffff;
      padding: 50px 40px;
      border-radius: 24px;
      text-align: center;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
      border: 2px solid transparent;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
    }

    .above-achievement-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #c62828, #ff5252);
      transform: scaleX(0);
      transition: transform 0.4s ease;
    }

    .above-achievement-card:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 60px rgba(198, 40, 40, 0.15);
      border-color: rgba(198, 40, 40, 0.2);
    }

    .above-achievement-card:hover::before {
      transform: scaleX(1);
    }

    .above-achievement-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #c62828, #ff5252);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 24px;
      color: #ffffff;
      font-size: 32px;
      box-shadow: 0 10px 30px rgba(198, 40, 40, 0.3);
      transition: transform 0.3s ease;
    }

    .above-achievement-card:hover .above-achievement-icon {
      transform: scale(1.1) rotate(10deg);
    }

    .above-achievement-year {
      font-family: 'Raleway', sans-serif;
      font-size: 18px;
      font-weight: 900;
      color: #c62828;
      margin-bottom: 16px;
      text-transform: uppercase;
      letter-spacing: 2px;
    }

    .above-achievement-title {
      font-family: 'Raleway', sans-serif;
      font-size: 24px;
      font-weight: 700;
      color: #1e2330;
      margin-bottom: 12px;
      line-height: 1.3;
    }

    .above-achievement-org {
      font-size: 16px;
      color: #4a4f5c;
      font-weight: 500;
    }

    /* Leadership Team */
    .above-leadership-team {
      padding: 100px 20px;
      background: #ffffff;
    }

    .above-team-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    .above-team-header {
      text-align: center;
      margin-bottom: 60px;
      }

    .above-team-badge {
      display: inline-block;
      background: rgba(198, 40, 40, 0.1);
      color: #c62828;
      padding: 10px 28px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 24px;
    }

    .above-team-title {
      font-family: 'Raleway', sans-serif;
      font-size: 48px;
      font-weight: 900;
      color: #1e2330;
      margin-bottom: 16px;
      line-height: 1.2;
    }

    .above-team-subtitle {
      font-size: 20px;
      color: #4a4f5c;
      max-width: 600px;
      margin: 0 auto;
    }

    .above-team-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 40px;
    }

    .above-team-card {
      background: #f8f9fa;
      border-radius: 24px;
      overflow: hidden;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: 2px solid transparent;
    }

    .above-team-card:hover {
      background: #ffffff;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
      border-color: rgba(198, 40, 40, 0.2);
    }

    .above-team-image {
      position: relative;
      width: 100%;
      height: 320px;
      overflow: hidden;
    }

    .above-team-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.6s ease;
    }

    .above-team-card:hover .above-team-image img {
      transform: scale(1.1);
    }

    .above-team-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(30, 35, 48, 0.9), transparent);
      display: flex;
      align-items: flex-end;
      justify-content: center;
      padding: 30px;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    .above-team-card:hover .above-team-overlay {
      opacity: 1;
    }

    .above-team-social {
      display: flex;
      gap: 16px;
    }

    .above-social-link {
      width: 44px;
      height: 44px;
      background: #ffffff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1e2330;
      transition: all 0.3s ease;
      font-size: 18px;
      text-decoration: none;
    }

    .above-social-link:hover {
      background: #c62828;
      color: #ffffff;
      transform: translateY(-5px);
    }

    .above-team-info {
      padding: 30px;
    }

    .above-team-name {
      font-family: 'Raleway', sans-serif;
      font-size: 24px;
      font-weight: 700;
      color: #1e2330;
      margin-bottom: 8px;
    }

    .above-team-role {
      color: #c62828;
      font-weight: 700;
      font-size: 14px;
      margin-bottom: 16px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }

    .above-team-bio {
      color: #4a4f5c;
      font-size: 15px;
      line-height: 1.7;
    }

    /* Company Culture Section */
    .above-cultures {
      padding: 100px 20px;
  background: #ffffffff;
      margin-top: -90px;
    }

    .above-culture-container {
      max-width: 1200px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 80px;
      align-items: center;
    }

    .above-culture-badge {
      display: inline-block;
      background: rgba(198, 40, 40, 0.1);
      color: #c62828;
      padding: 10px 28px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 24px;
    }

    .above-culture-title {
      font-family: 'Raleway', sans-serif;
      font-size: 33px;
      font-weight: 900;
      color: #1e2330;
      margin-bottom: 24px;
      line-height: 1.2;
      text-align: justify;
    }

    .above-culture-description {
      font-size: 18px;
      line-height: 1.8;
      color: #4a4f5c;
      margin-bottom: 40px;
      text-align: justify;
    }

    .above-culture-features {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    .above-culture-feature {
      display: flex;
      gap: 20px;
      align-items: flex-start;
      text-align: justify;
    }

    .above-feature-icon {
      width: 50px;
      height: 50px;
      background: linear-gradient(135deg, #c62828, #ff5252);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 24px;
      flex-shrink: 0;
    }

    .above-culture-feature h4 {
      font-family: 'Raleway', sans-serif;
      font-size: 20px;
      font-weight: 700;
      color: #1e2330;
      margin-bottom: 8px;
    }

    .above-culture-feature p {
      font-size: 16px;
      color: #4a4f5c;
      line-height: 1.6;
    }

    .above-culture-image {
      position: relative;
      border-radius: 24px;
      overflow: hidden;
      // box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    }

    .above-culture-image img {
      width: 100%;
      height: 500px;
      object-fit: cover;
      display: block;
      transition: transform 0.6s ease;
    }

    .above-culture-image:hover img {
      transform: scale(1.05);
    }

    /* CTA Section */
    .above-cta {
      padding: 100px 20px;
      background: linear-gradient(135deg, #1e2330 0%, #2d3546 100%);
      position: relative;
      overflow: hidden;
    }

    .above-cta::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -50%;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle, rgba(198, 40, 40, 0.1) 0%, transparent 70%);
      animation: rotateCTA 20s linear infinite;
    }

    @keyframes rotateCTA {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .above-cta-container {
      max-width: 900px;
      margin: 0 auto;
      text-align: center;
      position: relative;
      z-index: 1;
    }

    .above-cta-title {
      font-family: 'Raleway', sans-serif;
      font-size: 56px;
      font-weight: 900;
      color: #ffffff;
      margin-bottom: 24px;
      line-height: 1.2;
    }

    .above-cta-description {
      font-size: 20px;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 48px;
      max-width: 700px;
      margin-left: auto;
      margin-right: auto;
      line-height: 1.7;
    }

    .above-cta-buttons {
      display: flex;
      gap: 24px;
      justify-content: center;
      flex-wrap: wrap;
    }

    .above-cta-primary,
    .above-cta-secondary {
      padding: 18px 40px;
      border-radius: 50px;
      font-family: 'Raleway', sans-serif;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: none;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .above-cta-primary {
      background: linear-gradient(135deg, #c62828, #ff5252);
      color: #ffffff;
      box-shadow: 0 10px 30px rgba(198, 40, 40, 0.4);
    }

    .above-cta-primary:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 40px rgba(198, 40, 40, 0.5);
    }

    .above-cta-secondary {
      background: transparent;
      color: #ffffff;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    .above-cta-secondary:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #ffffff;
      transform: translateY(-5px);
    }

    /* Responsive Design */
    @media (max-width: 1200px) {
      .above-culture-container {
        gap: 60px;
      }
    }

    @media (max-width: 992px) {
      .about-hero-title {
        font-size: 48px;
      }

      .story-content-wrapper {
        grid-template-columns: 1fr;
        gap: 40px;
      }

      .story-timeline {
        display: none;
      }

      .story-cards {
        padding-left: 0;
      }

      .above-culture-container {
        grid-template-columns: 1fr;
        gap: 50px;
      }

      .above-culture-image {
        order: -1;
      }

      .above-team-grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .above-achievements-grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 768px) {
      .about-hero {
        padding: 60px 20px 40px;
      }

      .about-hero-title {
        font-size: 36px;
      }

      .about-hero-subtitle {
        font-size: 16px;
      }

      .about-hero-stats {
        gap: 30px;
      }

      .stat-numbers {
        font-size: 36px;
      }

      .story-title,
      .values-header h2,
      .above-team-title,
      .above-achievements-title,
      .above-culture-title,
      .above-cta-title {
        font-size: 32px;
      }

      .above-team-grid {
        grid-template-columns: 1fr;
      }

      .above-cta-buttons {
        flex-direction: column;
        align-items: center;
      }

      .above-cta-primary,
      .above-cta-secondary {
        width: 100%;
        max-width: 320px;
        justify-content: center;
      }

      .above-statistics-grid {
        grid-template-columns: 1fr;
        gap: 20px;
      }

      .above-culture-features {
        gap: 24px;
      }

      .enhanced-mission-vision {
        padding: 80px 20px;
      }
      
      .mv-title {
        font-size: 36px;
      }
      
      .mv-cards-container {
        grid-template-columns: 1fr;
        gap: 30px;
      }
      
      .mv-card {
        padding: 40px 30px;
      }
      
      .mv-card-icon-wrapper {
        width: 80px;
        height: 80px;
      }
      
      .mv-icon {
        font-size: 32px;
      }

      .floating-stat {
        display: none;
      }

      .image-caption {
        flex-direction: column;
        gap: 20px;
        text-align: center;
      }

      .achievement-badges {
        flex-direction: column;
        align-items: center;
      }
    }

    @media (max-width: 480px) {
      .about-hero-title {
        font-size: 28px;
      }

      .stat-itemm {
        width: 100%;
      }

      .mv-title {
        font-size: 28px;
      }
      
      .mv-card-title {
        font-size: 24px;
      }
      
      .mv-card-content {
        font-size: 16px;
      }
      
      .mv-card-highlights {
        flex-direction: column;
        align-items: center;
        gap: 10px;
      }

      .above-achievements-title,
      .above-team-title,
      .above-culture-title,
      .above-cta-title {
        font-size: 28px;
      }

      .above-cta-description {
        font-size: 16px;
      }

      .above-stat-number {
        font-size: 36px;
      }

      .above-culture-feature {
        flex-direction: column;
        text-align: center;
      }

      .above-feature-icon {
        margin: 0 auto;
      }

      .story-title {
        font-size: 28px;
      }

      .story-badge,
      .above-achievements-badge,
      .above-team-badge,
      .above-culture-badge {
        font-size: 12px;
        padding: 8px 20px;
      }
    }
  `}</style>
</div>
);
};
export default AboutUsPage;